import React, {
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  useState
} from 'react';
import { mapToStandardColor } from 'constants/colors';

export interface Tag {
  id: string;
  name: string;
  color: string;
}

const GAP = 8;
const MIN_TRUNCATED_DISPLAY = 30;
const BADGE_WIDTH_SM = 28;
const BADGE_WIDTH_MD = 36;
const BADGE_WIDTH_LG = 44;
const BADGE_THRESHOLD_MD = 10;
const BADGE_THRESHOLD_LG = 100;
const GAPS_NEEDED = 2;

function getBadgeWidth(hiddenCount: number): number {
  if (hiddenCount >= BADGE_THRESHOLD_LG) return BADGE_WIDTH_LG;
  if (hiddenCount >= BADGE_THRESHOLD_MD) return BADGE_WIDTH_MD;
  if (hiddenCount > 0) return BADGE_WIDTH_SM;
  return 0;
}

function fitsWithBadge(
  newUsed: number,
  remaining: number,
  containerWidth: number
): boolean {
  const estimatedBadgeWidth = getBadgeWidth(remaining);
  // gaps: one after the current tag, one between the next tag and the overflow badge
  return newUsed + GAPS_NEEDED * GAP + estimatedBadgeWidth <= containerWidth;
}

function adjustForTruncation(
  count: number,
  tagEls: HTMLElement[],
  usedWidth: number,
  containerWidth: number
): number {
  if (count <= 0 || count >= tagEls.length) return count;

  const hiddenCountAfter = tagEls.length - count - 1;
  const estimatedBadgeWidth = getBadgeWidth(hiddenCountAfter);
  const badgeSlot = estimatedBadgeWidth > 0 ? GAP + estimatedBadgeWidth : 0;
  const truncatedAvailable = containerWidth - usedWidth - GAP - badgeSlot;

  return truncatedAvailable < MIN_TRUNCATED_DISPLAY
    ? Math.max(0, count - 1)
    : count;
}

function computeVisibleCount(
  tagEls: HTMLElement[],
  containerWidth: number
): number {
  let usedWidth = 0;
  let count = 0;

  for (let i = 0; i < tagEls.length; i += 1) {
    const tagWidth = tagEls[i].offsetWidth;
    const isLast = i === tagEls.length - 1;
    const remaining = tagEls.length - i - 1;
    const newUsed = usedWidth + (i > 0 ? GAP : 0) + tagWidth;

    if (isLast) {
      if (newUsed <= containerWidth) count = tagEls.length;
      break;
    }

    const fits =
      remaining === 1
        ? newUsed + GAP <= containerWidth
        : fitsWithBadge(newUsed, remaining, containerWidth);

    if (fits) {
      usedWidth = newUsed;
      count = i + 1;
    } else {
      break;
    }
  }

  return adjustForTruncation(count, tagEls, usedWidth, containerWidth);
}

export interface TagChipProps {
  tag: Tag;
  truncate?: boolean;
}

export const TagChip: React.FC<TagChipProps> = ({ tag, truncate }) => {
  const colorMapping = mapToStandardColor(tag.color);

  return (
    <div
      style={{
        backgroundColor: colorMapping.background,
        color: colorMapping.text,
        borderRadius: '4px',
        padding: '0 8px',
        height: '20px',
        lineHeight: '20px',
        fontSize: '12px',
        fontWeight: 600,
        flexShrink: truncate ? 1 : 0,
        minWidth: truncate ? 0 : undefined,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
      title={tag.name}
    >
      {tag.name}
    </div>
  );
};

interface OverflowBadgeProps {
  count: number;
}

const OverflowBadge: React.FC<OverflowBadgeProps> = ({ count }) => {
  return (
    <div
      style={{
        backgroundColor: '#DFE2E7',
        color: '#4D5165',
        borderRadius: '4px',
        padding: '0 4px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        fontSize: '12px',
        fontWeight: 600,
        flexShrink: 0,
        minWidth: 'fit-content'
      }}
    >
      +{count}
    </div>
  );
};

interface TagsRowProps {
  tagIds?: string[];
  allTags: Tag[];
  wrap?: boolean;
}

const TagsRow: React.FC<TagsRowProps> = ({ tagIds, allTags, wrap = false }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);
  // Monotonic counter used to *force* a re-measure. A plain
  // `setVisibleCount(null)` cannot do this: when the count is already `null`
  // (e.g. we reset it on hide) React bails on the identical value and the
  // measurement effect never re-runs, leaving the row blank. Bumping this
  // nonce always produces a new value, so the effect is guaranteed to fire.
  const [remeasureNonce, setRemeasureNonce] = useState(0);

  const resolvedTags = useMemo(() => {
    if (!tagIds || !allTags) return [];
    return tagIds
      .map(id => allTags.find(t => t.id === id))
      .filter((tag): tag is Tag => Boolean(tag));
  }, [tagIds, allTags]);

  // Re-enter the measurement phase whenever the tag set changes so the hidden
  // measurement div is re-rendered before the measurement effect below runs.
  useLayoutEffect(() => {
    setVisibleCount(null);
  }, [resolvedTags]);

  // Measure while in the measurement phase (visibleCount === null). If the
  // container has no width yet — e.g. it is force-rendered inside an inactive
  // antd tab (`display: none`) — bail and stay in the measurement phase; the
  // ResizeObserver below bumps `remeasureNonce` once the tab becomes visible,
  // which re-runs this effect. Without that, `visibleCount` would stay stuck
  // at `null` and no tags would render until an unrelated re-render happened
  // to catch a non-zero width (which is why toggling the tab "fixed" the empty
  // row before this observer existed).
  useLayoutEffect(() => {
    if (visibleCount !== null) return;

    const container = containerRef.current;
    if (!container || resolvedTags.length === 0) {
      setVisibleCount(resolvedTags.length);
      return;
    }

    const containerWidth = container.offsetWidth;
    if (containerWidth === 0) return;

    const measureDiv = container.querySelector(
      '[data-measure-tags]'
    ) as HTMLDivElement;
    if (!measureDiv) return;

    const tagEls = Array.from(measureDiv.children) as HTMLElement[];
    setVisibleCount(computeVisibleCount(tagEls, containerWidth));
  }, [resolvedTags, visibleCount, remeasureNonce]);

  // Recompute when the container's width changes — most importantly when it
  // transitions from hidden (offsetWidth 0) to visible after a tab switch, and
  // back again. Resetting to the measurement phase (`null`) re-renders the
  // measurement div, while the nonce guarantees the measurement effect re-runs
  // even when the count was already `null`.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    let lastWidth = container.offsetWidth;
    const observer = new ResizeObserver(() => {
      const width = container.offsetWidth;
      if (width !== lastWidth) {
        lastWidth = width;
        setVisibleCount(null);
        setRemeasureNonce(nonce => nonce + 1);
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  if (resolvedTags.length === 0) {
    return null;
  }

  if (wrap) {
    return (
      <div className="w-full flex flex-wrap gap-2">
        {resolvedTags.map(tag => (
          <TagChip key={tag.id} tag={tag} />
        ))}
      </div>
    );
  }

  const hiddenCount =
    visibleCount === null ? 0 : resolvedTags.length - visibleCount - 1;
  const displayTags =
    visibleCount === null ? [] : resolvedTags.slice(0, visibleCount + 1);

  return (
    <div className="w-full overflow-hidden" ref={containerRef}>
      {/* Measurement phase: all tags rendered invisibly */}
      {visibleCount === null && (
        <div
          data-measure-tags
          style={{
            display: 'flex',
            gap: '8px',
            visibility: 'hidden',
            position: 'absolute',
            pointerEvents: 'none'
          }}
        >
          {resolvedTags.map(tag => (
            <TagChip key={tag.id} tag={tag} />
          ))}
        </div>
      )}

      {/* Display phase: only visible tags + badge */}
      {visibleCount !== null && (
        <div style={{ display: 'flex', gap: '8px', overflow: 'hidden' }}>
          {displayTags.map((tag, idx) => {
            const isLastVisible = idx === displayTags.length - 1;
            const hasExtraTags = resolvedTags.length > visibleCount;
            return (
              <TagChip
                key={tag.id}
                tag={tag}
                truncate={isLastVisible && hasExtraTags}
              />
            );
          })}
          {hiddenCount > 0 && <OverflowBadge count={hiddenCount} />}
        </div>
      )}
    </div>
  );
};

export default TagsRow;
